import React, { useState, useEffect } from 'react';
import CoachingConstants from 'coaching/CoachingConstants';
import { ComposableTable, getTextCellGenerator } from 'components/ComposableTable';
import { ParticipantRow } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import EventTracking from 'lib/EventTracking';
import Row from 'components/Row';
import ScrollLoadZone from 'components/ScrollLoadZone';

import { useCoachingViewState } from 'coaching/components/CoachingView';
import CoachingCoordinatorParticipantSidebarView from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantSidebarView';

export type Props = {
  loadingParticipants: boolean;
  participants: ParticipantRow[];
  onSelectParticipant?: (participant: ParticipantRow) => void;
  onNextPage: () => Promise<void>;
  hasNextPage?: boolean;
  selectedParticipant?: ParticipantRow;
  showScheduleSessionView: () => void;
};

function columnHeaders() {
  return [{ name: 'Student name' }, { name: 'Parent name' }, { name: 'Permalink slug' }];
}

function columnGenerators(): Array<(data: ParticipantRow) => React.ReactNode> {
  return [
    getTextCellGenerator('studentName', { weight: 'semibold' }),
    getTextCellGenerator('parentName'),
    getTextCellGenerator('distanceLearningPermalink'),
  ];
}

function trackEvent(event: string) {
  EventTracking.trackKey(
    `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.participant_list.${event}`
  );
}

export default function CoachingCoordinatorParticipantList(props: Props) {
  const { participants, showScheduleSessionView } = props;
  const [selectedParticipant, setSelectedParticipant] = useState<ParticipantRow | undefined>(
    props.selectedParticipant
  );
  const { closeSidebar, setSidebar } = useCoachingViewState();

  const onSelectParticipantSidebar = (selectedParticipant: ParticipantRow) => {
    setSelectedParticipant(selectedParticipant);
    setSidebar(
      <CoachingCoordinatorParticipantSidebarView
        participant={selectedParticipant}
        onShowScheduleSessionView={showScheduleSessionView}
      />
    );
  };

  useEffect(() => {
    trackEvent('view');

    return function cleanup() {
      closeSidebar();
    };
  }, []);

  useEffect(() => { }, [selectedParticipant?.uuid]);
  const participantsWithOrganizationIdentifier = participants.map((participant) => {
    const participantPartialInfo = { studentName: `${participant.studentName}${participant.enrolledInOrganizationTutoring ? ' (org)' : ''}` }
    return {
      ...participant,
      ...participantPartialInfo
    }
  })

  return (
    <React.Fragment>
      <Row>
        <ComposableTable<ParticipantRow>
          rows={participantsWithOrganizationIdentifier}
          columnHeaders={columnHeaders()}
          columnGenerators={columnGenerators()}
          fixedHeader
          keyField="uuid"
          handleRowClick={participant => {
            trackEvent('participant.click');
            props.onSelectParticipant?.(participant);
            onSelectParticipantSidebar(participant);
          }}
          keysOfActiveRows={selectedParticipant && [selectedParticipant.uuid]}
        />
      </Row>
      {props.loadingParticipants && <CenteredLoadingIndicator />}
      {props.hasNextPage && <ScrollLoadZone threshold={100} onZoneActivated={props.onNextPage} />}
    </React.Fragment>
  );
}
